import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Spinner,
  Badge,
  Form,
  Table,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import { getCustomerExportAnalysis } from "../../../api/disticaret/orderApi";
import { getCustomers } from "../../../api/disticaret/customerApi";

// Grafik Kütüphaneleri
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
} from "chart.js";
import { Pie} from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement
);

export default function CustomerAnalysisPage() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  useEffect(() => {
    fetchCustomersList();
  }, []);

  const fetchCustomersList = async () => {
    try {
      const response = await getCustomers();
      if (response.data?.success || response.data?.statusCode === 200) {
        setCustomers(response.data.data);
      }
    } catch (error) {
      console.error("Müşteri listesi çekilemedi", error);
    }
  };

  const fetchAnalysis = async (customerId) => {
    setLoading(true);
    try {
      // Backend'den gelen güncel analiz verisi
      const response = await getCustomerExportAnalysis({ customerId });
      if (response.data?.success || response.data?.statusCode === 200) {
        setData(response.data.data);
      } else {
        setData(null);
      }
    } catch (error) {
      console.error("Analiz verisi çekme hatası", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (e) => {
    const val = e.target.value;
    setSelectedCustomerId(val);
    if (val) fetchAnalysis(val);
    else setData(null);
  };

  const summary = data?.summary || {};
  const finance = data?.finance || {};
  const charts = data?.charts || {};
  const risk = data?.risk || {};
  const lastOrders = data?.lastOrders || [];

  const fmtNum = (val) => {
    const num = Number(val);
    if (isNaN(num)) return "0,00";
    return num.toLocaleString("tr-TR", { minimumFractionDigits: 2 });
  };

  const renderStatusBadge = (status) => {
    let bg = "secondary";
    const s = status?.toLowerCase();
    if (s === "completed" || s === "tamamlandi") bg = "success";
    else if (s === "production" || s === "uretiliyor") bg = "warning";
    else if (s === "planning") bg = "primary";

    return (
      <Badge bg={bg} className="text-uppercase" style={{ fontSize: "11px" }}>
        {status || "Bilinmiyor"}
      </Badge>
    );
  };

  const getRiskBadgeColor = (status) => {
    if (status === "blocked") return "dark";
    if (status === "risky") return "danger";
    if (status === "watch") return "warning";
    return "success";
  };

  const pieData = charts?.productionStatus
    ? {
        labels: charts.productionStatus.labels,
        datasets: [
          {
            data: charts.productionStatus.data,
            backgroundColor: ["#28a745", "#dc3545"],
            borderWidth: 1,
          },
        ],
      }
    : null;

  return (
    <Layout>
      <Container
        fluid
        className="py-4 px-lg-5"
        style={{ backgroundColor: "#F4F6F9", minHeight: "100vh" }}
      >
        {/* HEADER */}
        <Row className="align-items-center mb-4">
          <Col md={8}>
            <h3 className="fw-bold text-dark mb-1">Müşteri İhracat Analizi</h3>
            <p className="text-muted small mb-0">
              Üretim ve Finansal Risk Özeti
            </p>
          </Col>
          <Col md={4} className="d-flex justify-content-md-end mt-3 mt-md-0">
            <Form.Select
              value={selectedCustomerId}
              onChange={handleCustomerChange}
              className="shadow-sm border-0 fw-bold"
            >
              <option value="">-- Müşteri Seçiniz --</option>
              {customers.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.companyName}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {!selectedCustomerId ? (
          <div className="text-center py-5 mt-5 text-muted">
            <h5>Analiz için müşteri seçiniz.</h5>
          </div>
        ) : loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            {/* KPI KARTLARI */}
            <Row className="g-3 mb-4 text-center">
              <Col md={2}>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderLeft: "4px solid #6c757d" }}
                >
                  <Card.Body className="p-3">
                    <p className="text-muted small fw-bold mb-1">SİPARİŞ</p>
                    <h4 className="fw-bold mb-0">{summary?.totalOrders}</h4>
                    <span
                      className="text-muted small"
                      style={{ fontSize: "10px" }}
                    >
                      Aktif: {summary?.activeOrders}
                    </span>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderLeft: "4px solid #17a2b8" }}
                >
                  <Card.Body className="p-3">
                    <p className="text-muted small fw-bold mb-1">
                      TOPLAM KALEM
                    </p>
                    <h4 className="fw-bold mb-0">{summary?.totalItems}</h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderLeft: "4px solid #007bff" }}
                >
                  <Card.Body className="p-3">
                    <p className="text-muted small fw-bold mb-1">
                      TOPLAM TONAJ
                    </p>
                    <h4 className="fw-bold mb-0">{summary?.totalTons}</h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderLeft: "4px solid #28a745" }}
                >
                  <Card.Body className="p-3">
                    <p className="text-muted small fw-bold mb-1">
                      ÜRETİLEN (T)
                    </p>
                    <h4 className="fw-bold mb-0 text-success">
                      {summary?.producedTons}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderLeft: "4px solid #dc3545" }}
                >
                  <Card.Body className="p-3">
                    <p className="text-muted small fw-bold mb-1">KALAN (T)</p>
                    <h4 className="fw-bold mb-0 text-danger">
                      {summary?.notProducedTons}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="border-0 shadow-sm h-100 bg-primary text-white">
                  <Card.Body className="p-3">
                    <p className="small fw-bold mb-1 text-white-50">
                      TAMAMLANMA
                    </p>
                    <h4 className="fw-bold mb-0">% {summary?.completion}</h4>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-4 mb-4">
              {/* ÜRETİM PASTA GRAFİĞİ */}
              <Col md={4}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <h6 className="fw-bold text-secondary mb-4 text-center">
                      Üretim Statüsü (Ton)
                    </h6>
                    <div style={{ height: "220px" }}>
                      {pieData && (
                        <Pie
                          data={pieData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                          }}
                        />
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* FİNANSAL RİSK PANELİ */}
              <Col md={8}>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{
                    borderTop: `4px solid var(--bs-${getRiskBadgeColor(
                      risk?.riskStatus
                    )})`,
                  }}
                >
                  <Card.Body>
                    <h6 className="fw-bold text-secondary mb-4">
                      Finans ve Risk Profili
                    </h6>
                    <Row className="g-3">
                      <Col sm={6} className="border-end">
                        <div className="mb-3 d-flex justify-content-between">
                          <span className="text-muted">Risk Durumu:</span>
                          <Badge
                            bg={getRiskBadgeColor(risk?.riskStatus)}
                            className="text-uppercase"
                          >
                            {risk?.riskStatus || "Safe"}
                          </Badge>
                        </div>
                        <div className="mb-3 d-flex justify-content-between">
                          <span className="text-muted">Risk Skoru:</span>
                          <span className="fw-bold">{risk?.riskScore}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Toplam Ödeme:</span>
                          <span className="fw-bold">
                            {finance?.totalPayments}
                          </span>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="mb-2 d-flex justify-content-between">
                          <span className="text-muted small">
                            Bekleyen Tutar:
                          </span>
                          <span className="fw-bold text-warning">
                            ${fmtNum(finance?.pendingAmount)}
                          </span>
                        </div>
                        <div className="mb-2 d-flex justify-content-between">
                          <span className="text-muted small">
                            Geciken Tutar:
                          </span>
                          <span className="fw-bold text-danger">
                            ${fmtNum(finance?.overdueAmount)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between bg-light p-2 rounded">
                          <span className="fw-bold small">
                            Toplam Açık Risk:
                          </span>
                          <span className="fw-bold text-primary">
                            ${fmtNum(finance?.exposure)}
                          </span>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* SON SİPARİŞLER (ITEM BAZLI TARİHLİ) */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h6 className="fw-bold text-secondary mb-3">
                  Son Siparişler ve Kalemleri
                </h6>
                <Table
                  responsive
                  hover
                  size="sm"
                  className="text-center align-middle"
                >
                  <thead className="bg-light small">
                    <tr>
                      <th>Proforma No</th>
                      <th>Toplam Tutar</th>
                      <th>Döviz</th>
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody className="small">
                    {lastOrders.map((o, idx) => (
                      <tr key={idx}>
                        <td className="fw-bold">{o.proformaNo}</td>
                        <td>{fmtNum(o.totalAmount)}</td>
                        <td>
                          <Badge bg="secondary">{o.currency}</Badge>
                        </td>
                        <td>{renderStatusBadge(o.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </Layout>
  );
}
